// ---------------------------------------------------------------------------
// Excel render interpreter: (template JSON + record[s]) -> ExcelJS workbook ->
// browser download. ExcelJS is lazy-imported so it stays out of the main bundle.
// ---------------------------------------------------------------------------

import { getDerived, RECORD_LIST_PATH } from "../fieldCatalog";
import {
  resolvePath,
  coerceCell,
  interpolate,
  computeDerived,
  buildTableRows,
  cellValue,
  sumCells,
} from "./valueResolver";

const DEFAULT_WIDTH = 18;

function effectiveCols(template) {
  let cols = (template.sheet && template.sheet.columnCount) || 1;
  for (const s of template.sections) {
    if (s.type === "DATA_TABLE") cols = Math.max(cols, (s.columns || []).length);
    if (s.type === "DETAIL_FIELDS") cols = Math.max(cols, (s.columnsPerRow || 2) * 2);
  }
  return Math.max(cols, 2);
}

function sheetOpts(template) {
  return {
    currencyFormat: (template.sheet && template.sheet.currencyFormat) || "#,##0.00",
    dateFormat: (template.sheet && template.sheet.dateFormat) || "yyyy-mm-dd",
  };
}

/** Apply a builder style object to an ExcelJS cell. */
function applyStyle(cell, style, defaultFont) {
  style = style || {};
  const font = {};
  if (defaultFont) {
    if (defaultFont.name) font.name = defaultFont.name;
    if (defaultFont.size) font.size = defaultFont.size;
  }
  if (style.bold) font.bold = true;
  if (style.italic) font.italic = true;
  if (style.underline) font.underline = true;
  if (style.fontSize) font.size = style.fontSize;
  if (style.fontFamily) font.name = style.fontFamily;
  if (style.fontColor) font.color = { argb: style.fontColor };
  if (Object.keys(font).length) cell.font = font;

  const align = {};
  if (style.align) align.horizontal = style.align;
  if (style.valign) align.vertical = style.valign;
  if (style.wrapText) align.wrapText = true;
  if (Object.keys(align).length) cell.alignment = align;

  if (style.fillColor) {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: style.fillColor } };
  }
  if (style.border) {
    const b = { style: style.border === true ? "thin" : style.border };
    cell.border = { top: b, left: b, bottom: b, right: b };
  }
}

function renderTitle(ws, sec, record, template, r, cols) {
  const cell = ws.getCell(r, 1);
  cell.value = interpolate(sec.text, record);
  if (sec.merge !== false && cols > 1) ws.mergeCells(r, 1, r, cols);
  applyStyle(cell, sec.style, template.sheet.defaultFont);
  ws.getRow(r).height = 22;
  return r + 1;
}

function renderText(ws, sec, record, template, r) {
  const cell = ws.getCell(r, 1);
  cell.value = interpolate(sec.text, record);
  applyStyle(cell, sec.style, template.sheet.defaultFont);
  return r + 1;
}

function renderDetail(ws, sec, record, template, r) {
  const fields = sec.fields || [];
  if (!fields.length) return r;
  const per = sec.columnsPerRow || 2;
  const opts = sheetOpts(template);
  let row = r;
  let inRow = 0;
  fields.forEach((fl) => {
    const c = inRow * 2 + 1;
    const labelCell = ws.getCell(row, c);
    labelCell.value = `${fl.label}:`;
    applyStyle(labelCell, { bold: true, ...(sec.labelStyle || {}) }, template.sheet.defaultFont);

    const { value, numFmt } = coerceCell(resolvePath(record, fl.path), fl.type, opts);
    const valCell = ws.getCell(row, c + 1);
    valCell.value = value;
    if (numFmt) valCell.numFmt = numFmt;
    applyStyle(valCell, sec.valueStyle, template.sheet.defaultFont);

    inRow += 1;
    if (inRow === per) {
      row += 1;
      inRow = 0;
    }
  });
  if (inRow > 0) row += 1;
  return row;
}

function renderTable(ws, sec, record, template, r, listItems) {
  const columns = sec.columns || [];
  if (!columns.length) return r;
  const opts = sheetOpts(template);

  // header
  columns.forEach((col, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = col.header;
    applyStyle(cell, { bold: true, ...(sec.headerStyle || {}), align: col.align }, template.sheet.defaultFont);
  });
  let row = r + 1;

  // Rows joined parent→child: order-level columns repeat on each element row.
  const rowPairs = buildTableRows(record, sec, listItems);
  rowPairs.forEach((rp, ri) => {
    columns.forEach((col, ci) => {
      const { value, numFmt } = coerceCell(cellValue(rp, col), col.type, opts);
      const cell = ws.getCell(row, ci + 1);
      cell.value = value;
      if (col.numberFormat) cell.numFmt = col.numberFormat;
      else if (numFmt) cell.numFmt = numFmt;
      const zebra = sec.zebra && ri % 2 === 1 ? { fillColor: "FFF6F8FC" } : {};
      applyStyle(cell, { align: col.align, ...(col.style || {}), ...zebra }, template.sheet.defaultFont);
    });
    row += 1;
  });

  if (sec.showColumnTotals && rowPairs.length) {
    columns.forEach((col, ci) => {
      const cell = ws.getCell(row, ci + 1);
      if (ci === 0) {
        cell.value = "TOTAL";
      } else if (col.total) {
        cell.value = sumCells(rowPairs, col);
        cell.numFmt = col.numberFormat || (col.type === "currency" ? opts.currencyFormat : undefined);
      }
      applyStyle(cell, { bold: true, fillColor: "FFEDEFFB", align: ci === 0 ? "left" : col.align }, template.sheet.defaultFont);
    });
    row += 1;
  }
  return row;
}

function renderFooter(ws, sec, record, template, r, cols) {
  const rows = sec.rows || [];
  if (!rows.length) return r;
  const opts = sheetOpts(template);
  const derived = getDerived(template.source);
  const labelCol = Math.max(1, cols - 1);
  const valueCol = cols;
  let row = r;
  rows.forEach((fr) => {
    const raw = fr.derived ? computeDerived(record, derived[fr.derived]) : resolvePath(record, fr.path);
    const { value, numFmt } = coerceCell(raw, fr.type || "currency", opts);

    const labelCell = ws.getCell(row, labelCol);
    labelCell.value = `${fr.label}:`;
    applyStyle(labelCell, { bold: !!(fr.style && fr.style.bold), align: "right" }, template.sheet.defaultFont);

    const valueCell = ws.getCell(row, valueCol);
    valueCell.value = value;
    if (numFmt) valueCell.numFmt = numFmt;
    applyStyle(valueCell, { bold: !!(fr.style && fr.style.bold), align: "right" }, template.sheet.defaultFont);
    row += 1;
  });
  return row;
}

function renderSections(ws, template, record, startRow, cols, recordList) {
  let r = startRow;
  for (const sec of template.sections) {
    switch (sec.type) {
      case "TITLE":
        r = renderTitle(ws, sec, record, template, r, cols);
        break;
      case "DETAIL_FIELDS":
        r = renderDetail(ws, sec, record, template, r);
        break;
      case "DATA_TABLE":
        r = renderTable(ws, sec, record, template, r, sec.collectionPath === RECORD_LIST_PATH ? recordList : null);
        break;
      case "FOOTER_TOTALS":
        r = renderFooter(ws, sec, record, template, r, cols);
        break;
      case "TEXT":
        r = renderText(ws, sec, record, template, r);
        break;
      case "SPACER":
        r += sec.rows || 1;
        break;
      default:
        break;
    }
  }
  return r;
}

function applyColumnWidths(ws, template, cols) {
  const widths = (template.sheet && template.sheet.columnWidths) || [];
  for (let i = 0; i < cols; i += 1) {
    ws.getColumn(i + 1).width = widths[i] || DEFAULT_WIDTH;
  }
}

async function buildWorkbook(template, records) {
  const mod = await import("exceljs");
  const ExcelJS = mod.default || mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = "ProductSell Report Builder";
  const ws = wb.addWorksheet((template.sheet && template.sheet.sheetName) || "Report");
  const cols = effectiveCols(template);
  applyColumnWidths(ws, template, cols);

  const list = records && records.length ? records : [];
  const isListReport = template.sections.some(
    (s) => s.type === "DATA_TABLE" && s.collectionPath === RECORD_LIST_PATH
  );

  if (isListReport) {
    // One flat report: the list-table iterates every record. Non-list sections
    // (title/text/etc.) render once against the first record for context.
    const ctx = list.length ? list[0] : {};
    renderSections(ws, template, ctx, 1, cols, list);
  } else {
    // Per-record report: stack each record's full layout.
    let r = 1;
    list.forEach((record, idx) => {
      if (idx > 0) r += 1; // blank spacer row between stacked records (bulk)
      r = renderSections(ws, template, record, r, cols, null);
    });
  }
  return wb;
}

function safeName(template) {
  const base = (template && template.name) || "report";
  return `${base.replace(/[^\w.-]+/g, "_")}.xlsx`;
}

async function downloadWorkbook(wb, fileName) {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Generate + download an xlsx for a single record. */
export async function generateExcel(template, record, fileName) {
  const wb = await buildWorkbook(template, [record]);
  await downloadWorkbook(wb, fileName || safeName(template));
}

/** Generate + download an xlsx stacking many records (bulk / date-range). */
export async function generateExcelForMany(template, records, fileName) {
  const wb = await buildWorkbook(template, records || []);
  await downloadWorkbook(wb, fileName || safeName(template));
}

/**
 * One flat sheet from pre-flattened rows (each row = order fields + line-item
 * fields merged). Renders an optional title + a single table. Used for
 * cross-order Line-Items reports over a date range.
 */
export async function generateExcelFromFlatRows(template, tableSec, flatRows, fileName) {
  const mod = await import("exceljs");
  const ExcelJS = mod.default || mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = "ProductSell Report Builder";
  const ws = wb.addWorksheet((template.sheet && template.sheet.sheetName) || "Report");
  const columns = (tableSec && tableSec.columns) || [];
  const opts = sheetOpts(template);
  for (let i = 0; i < columns.length; i += 1) {
    const widths = (template.sheet && template.sheet.columnWidths) || [];
    ws.getColumn(i + 1).width = widths[i] || DEFAULT_WIDTH;
  }

  let row = 1;
  const titleSec = (template.sections || []).find((s) => s.type === "TITLE");
  if (titleSec && columns.length) {
    const cell = ws.getCell(row, 1);
    cell.value = interpolate(titleSec.text, flatRows[0] || {});
    if (columns.length > 1) ws.mergeCells(row, 1, row, columns.length);
    applyStyle(cell, titleSec.style, template.sheet.defaultFont);
    ws.getRow(row).height = 22;
    row += 1;
  }

  columns.forEach((col, i) => {
    const cell = ws.getCell(row, i + 1);
    cell.value = col.header;
    applyStyle(cell, { bold: true, ...(tableSec.headerStyle || {}), align: col.align }, template.sheet.defaultFont);
  });
  row += 1;

  flatRows.forEach((r, ri) => {
    const rp = { rec: r, el: r };
    columns.forEach((col, ci) => {
      const { value, numFmt } = coerceCell(cellValue(rp, col), col.type, opts);
      const cell = ws.getCell(row, ci + 1);
      cell.value = value;
      if (col.numberFormat) cell.numFmt = col.numberFormat;
      else if (numFmt) cell.numFmt = numFmt;
      const zebra = tableSec.zebra && ri % 2 === 1 ? { fillColor: "FFF6F8FC" } : {};
      applyStyle(cell, { align: col.align, ...(col.style || {}), ...zebra }, template.sheet.defaultFont);
    });
    row += 1;
  });

  if (tableSec.showColumnTotals && flatRows.length) {
    const pairs = flatRows.map((r) => ({ rec: r, el: r }));
    columns.forEach((col, ci) => {
      const cell = ws.getCell(row, ci + 1);
      if (ci === 0) cell.value = "TOTAL";
      else if (col.total) {
        cell.value = sumCells(pairs, col);
        cell.numFmt = col.numberFormat || (col.type === "currency" ? opts.currencyFormat : undefined);
      }
      applyStyle(cell, { bold: true, fillColor: "FFEDEFFB", align: ci === 0 ? "left" : col.align }, template.sheet.defaultFont);
    });
    row += 1;
  }

  await downloadWorkbook(wb, fileName || safeName(template));
}
